/**
 * Search Tab - Search for user presence details
 */

import { useState } from 'react';
import { IconSearch } from '@tabler/icons-react';
import { Stack, Paper, TextInput, Button, Group, Text, Divider, Grid, Badge } from '../../../../components/ui';
import { FadeIn } from '../../../../components/animations';
import { usePresenceData } from '../hooks/usePresenceData';
import { IdleTimeDisplay } from '../../../../components/ui/IdleTimeDisplay';

export function SearchTab() {
  const [searchUserId, setSearchUserId] = useState('');
  const { selectedUserStats, fetchUserDetails, clearUserDetails, loading } = usePresenceData();

  const handleSearch = () => {
    if (searchUserId.trim()) {
      fetchUserDetails(searchUserId.trim());
    }
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h ${minutes}m`;
    }

    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  return (
    <FadeIn>
      <Stack gap="md">
        {/* Search Input */}
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Search User Presence
            </Text>
            <Group>
              <TextInput
                placeholder="Enter user ID..."
                value={searchUserId}
                onChange={(e) => setSearchUserId(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1 }}
                leftSection={<IconSearch size={16} />}
              />
              <Button onClick={handleSearch} loading={loading}>
                Search
              </Button>
              {selectedUserStats && (
                <Button variant="light" onClick={clearUserDetails}>
                  Clear
                </Button>
              )}
            </Group>
          </Stack>
        </Paper>

        {/* Results */}
        {selectedUserStats && (
          <Paper shadow="sm" p="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text size="lg" fw={600}>
                  User: {selectedUserStats.userId}
                </Text>
                <IdleTimeDisplay lastSeen={selectedUserStats.lastSeen} />
              </Group>

              <Divider />

              {/* Stats Grid */}
              <Grid gutter="md">
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper p="sm" withBorder>
                    <Text size="xs" c="dimmed">
                      Total Online Time
                    </Text>
                    <Text size="lg" fw={700}>
                      {formatDuration(selectedUserStats.totalOnlineTime)}
                    </Text>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper p="sm" withBorder>
                    <Text size="xs" c="dimmed">
                      Status Changes
                    </Text>
                    <Text size="lg" fw={700}>
                      {selectedUserStats.totalStatusChanges}
                    </Text>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper p="sm" withBorder>
                    <Text size="xs" c="dimmed">
                      Avg Session
                    </Text>
                    <Text size="lg" fw={700}>
                      {formatDuration(selectedUserStats.averageSessionDuration)}
                    </Text>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper p="sm" withBorder>
                    <Text size="xs" c="dimmed">
                      Longest Session
                    </Text>
                    <Text size="lg" fw={700}>
                      {formatDuration(selectedUserStats.longestSession)}
                    </Text>
                  </Paper>
                </Grid.Col>
              </Grid>

              {/* Status Breakdown */}
              <div>
                <Text size="sm" fw={600} mb="xs">
                  Status Breakdown
                </Text>
                <Group gap="xs">
                  {Object.entries(selectedUserStats.statusBreakdown || {}).map(([status, count]) => (
                    <Badge key={status} variant="light">
                      {status}: {count}
                    </Badge>
                  ))}
                </Group>
              </div>

              {/* Platform Breakdown */}
              <div>
                <Text size="sm" fw={600} mb="xs">
                  Platform Usage
                </Text>
                <Group gap="xs">
                  {Object.entries(selectedUserStats.platformBreakdown || {}).map(([platform, count]) => (
                    <Badge key={platform} variant="outline">
                      {platform}: {count}
                    </Badge>
                  ))}
                </Group>
              </div>
            </Stack>
          </Paper>
        )}

        {!selectedUserStats && !loading && (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Stack align="center" gap="xs">
              <IconSearch size={48} color="var(--mantine-color-gray-5)" />
              <Text size="sm" c="dimmed" ta="center">
                Enter a user ID to view their presence statistics
              </Text>
            </Stack>
          </Paper>
        )}
      </Stack>
    </FadeIn>
  );
}
